import React, { useState, useEffect, useContext } from 'react';
import { CameraController, CameraViews } from '../../camera-controller.js';
import { Configuration } from '../../data/configuration.js';
import { VehicleView } from '../vehicle-view.js';
import { ExteriorConfigurator } from '../../configurator/exterior-configurator.js';
import { ExteriorTextilesView } from './exterior-textiles-view.js';
import { GraphicsView } from './graphics-view.js';
import { FinalFinishesView } from './final-finishes-view.js';
import { AppContext } from '../app.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

enum Views {
	Exterior,
	ExteriorTextiles,
	Graphics,
	FinalFinishes
}

enum MainViewOptions {
	ExteriorTextiles,
	Graphics,
	FinalFinishes
}

interface ExteriorViewProps {
	cameraController: CameraController;
	exteriorConfigurator: ExteriorConfigurator;
	onBackButtonClicked: () => void;
	onNextButtonClicked: () => void;
}

interface MainViewProps {
	onOptionClicked: (option: MainViewOptions) => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function ExteriorView(props: ExteriorViewProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	// states.
	const [view, setView] = useState<Views>(Views.Exterior);

	// hide back button when a "child" section is selected.
	useEffect(() => {
		const prevBtn = document.getElementById('vehicle-summary-prev');
		if (!prevBtn) return;

		if (view !== Views.Exterior) {
			prevBtn.style.display = 'none';
		} else {
			prevBtn.style.display = 'block';
		}
	}, [view]);

	// return the HTML.
	switch (view) {
		case Views.Exterior:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.HoodView);

			// return the HTML.
			return (
				<VehicleView
					onBackButtonClicked={props.onBackButtonClicked}
					onNextButtonClicked={props.onNextButtonClicked}>
					<MainView
						onOptionClicked={(option: MainViewOptions) => {
							switch (option) {
								case MainViewOptions.ExteriorTextiles:
									setView(Views.ExteriorTextiles);
									break;
								case MainViewOptions.Graphics:
									setView(Views.Graphics);
									break;
								case MainViewOptions.FinalFinishes:
									setView(Views.FinalFinishes);
									break;
							}
						}}
					/>
				</VehicleView>
			);

		case Views.ExteriorTextiles:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.HoodView);

			// return the HTML.
			return (
				<ExteriorTextilesView
					exteriorConfigurator={props.exteriorConfigurator}
					onBackButtonClicked={() => {
						setView(Views.Exterior);
					}}
				/>
			);

		case Views.Graphics:

			// transition the camera to the relevant viewpoint.
			if (Configuration.isSupersport400Model()) {
				props.cameraController.changeCameraView(CameraViews.S400GraphicsView);
			}
			else {
				props.cameraController.changeCameraView(CameraViews.ModelView);
			}

			// return the HTML.
			return (
				<GraphicsView
					exteriorConfigurator={props.exteriorConfigurator}
					onBackButtonClicked={() => {
						setView(Views.Exterior);
					}}
				/>
			);

		case Views.FinalFinishes:

			// transition the camera to the relevant viewpoint.
			props.cameraController.changeCameraView(CameraViews.FinalFinishesView);

			// return the HTML.
			return (
				<FinalFinishesView
					exteriorConfigurator={props.exteriorConfigurator}
					cameraController={props.cameraController}
					onBackButtonClicked={() => {
						setView(Views.Exterior);
					}}
				/>
			);

		default:
			return null;
	}
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function MainView(props: MainViewProps) {
	return (
		<div id="view-content">
			<div id="view-content-header">
				<h2 id="ga4-exterior-options" style={{ marginBottom: 0 }}>
					EXTERIOR OPTIONS
				</h2>
			</div>
			<div className="view-content-options-rows">
				<div className="view-content-options-row">
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MainViewOptions.ExteriorTextiles);
						}}>
						<img src="assets/images/configuration-items/exterior/options/hood.webp" width="100%" height="auto" alt="Roof" />
						<h4>ROOF</h4>
					</a>
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MainViewOptions.Graphics);
						}}>
						<img src="assets/images/configuration-items/exterior/options/graphics.webp" width="100%" height="auto" alt="Graphics" />
						<h4>GRAPHICS</h4>
					</a>
				</div>
				<div className="view-content-options-row">
					<a role="button" className="view-content-options-item"
						onClick={() => {
							props.onOptionClicked(MainViewOptions.FinalFinishes);
						}}>
						<img src="assets/images/configuration-items/exterior/options/final-finishes.webp" width="100%" height="auto" alt="Final Finishes" />
						<h4>FINAL FINISHES</h4>
					</a>
					<div className="view-content-options-item" />
				</div>
			</div>
		</div>
	);
}
